import { useState, useEffect, useCallback, useRef } from 'react'
import {
  History, GraduationCap, Loader2, Calendar, Landmark, AlertCircle,
  User, Search, X, ArrowLeft, Mail, Phone, MapPin, ExternalLink,
  BookOpen, Award
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi } from '@/services/api'
import { useNavigate } from 'react-router-dom'

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE — synchronisées avec DashboardHeader
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// ─── Helpers de récupération localStorage ───
const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

interface FormerStudent {
  id: number
  matricule: string
  first_name: string
  last_name: string
  sex: string
  date_of_birth: string
  class_name?: string
  school_name?: string
  graduation_year?: string
  diploma_type?: string
  post_bac_path?: string
  contact_email?: string
  contact_phone?: string
  current_city?: string
  status: string
  academic_year_id?: number
  school_id?: number
}

export default function StudentFormerPage() {
  const { lang } = useLang()
  const navigate = useNavigate()

  // ─── RÉCUPÉRATION DEPUIS LOCALSTORAGE ───
  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '')

  const [students, setStudents] = useState<FormerStudent[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStudent, setSelectedStudent] = useState<FormerStudent | null>(null)

  // ─── REFS pour éviter les boucles infinies ───
  const schoolIdRef = useRef<number | null>(schoolId)
  const yearIdRef = useRef<number | null>(academicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = academicYearId }, [academicYearId])

  const $t = {
    fr: {
      title: 'Anciens élèves',
      subtitle: 'Alumni et diplômés',
      loading: 'Chargement...',
      error: 'Erreur de chargement',
      noData: 'Aucun ancien élève',
      noDataDesc: 'Aucun ancien élève trouvé pour cette année scolaire.',
      school: 'Établissement',
      academicYear: 'Année scolaire',
      noYear: 'Aucune année sélectionnée',
      noSchool: 'Aucun établissement sélectionné',
      back: 'Retour',
      search: 'Rechercher...',
      matricule: 'Matricule',
      name: 'Nom complet',
      sex: 'Sexe',
      birthDate: 'Date de naissance',
      class: 'Classe de départ',
      graduationYear: 'Année de diplôme',
      diploma: 'Diplôme',
      postBac: 'Parcours post-bac',
      contact: 'Contact',
      email: 'Email',
      phone: 'Téléphone',
      city: 'Ville actuelle',
      registry: 'Registre des anciens élèves',
      registryDesc: 'Registre de tous les anciens élèves diplômés avec leurs parcours post-bac et coordonnées.',
      details: 'Détails',
      close: 'Fermer',
    },
    en: {
      title: 'Former Students',
      subtitle: 'Alumni and graduates',
      loading: 'Loading...',
      error: 'Loading error',
      noData: 'No former students',
      noDataDesc: 'No former students found for this academic year.',
      school: 'School',
      academicYear: 'Academic Year',
      noYear: 'No year selected',
      noSchool: 'No school selected',
      back: 'Back',
      search: 'Search...',
      matricule: 'Reg. No.',
      name: 'Full Name',
      sex: 'Sex',
      birthDate: 'Date of Birth',
      class: 'Graduating class',
      graduationYear: 'Graduation Year',
      diploma: 'Diploma',
      postBac: 'Post-bac path',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      city: 'Current city',
      registry: 'Former Students Registry',
      registryDesc: 'Registry of all graduated former students with their post-bac paths and contact details.',
      details: 'Details',
      close: 'Close',
    }
  }[lang]

  // ─── CHARGEMENT DES DONNÉES — exécuté UNE SEULE FOIS au montage ───
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const currentSchoolId = schoolIdRef.current
      const currentYearId = yearIdRef.current

      const params: Record<string, any> = { status: 'diplome' }
      if (currentSchoolId) params.school_id = currentSchoolId
      if (currentYearId) params.academic_year_id = currentYearId

      const res = await studentsApi.getAll(params)
      const items = res?.items || res?.data || res || []
      setStudents(Array.isArray(items) ? items : [])
    } catch (err) {
      console.error('Erreur chargement anciens élèves:', err)
      toast.error($t.error)
    } finally {
      setLoading(false)
    }
  }, [$t.error])

  useEffect(() => { loadData() }, [loadData])

  // ─── ÉCOUTE DES CHANGEMENTS D'ANNÉE (CustomEvent + localStorage) ───
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      const currentYearId = yearIdRef.current
      const currentSchoolId = schoolIdRef.current

      let changed = false

      if (newYearId && newYearId !== currentYearId) {
        setAcademicYearId(newYearId)
        yearIdRef.current = newYearId
        const label = detail.label || getStoredYearLabel() || ''
        setAcademicYearName(label)
        changed = true
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== currentSchoolId) {
        setSchoolId(newSchoolId)
        schoolIdRef.current = newSchoolId
        changed = true
      }

      if (changed) {
        setSelectedStudent(null)
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadData()
      }
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      let changed = false

      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        const currentYearId = yearIdRef.current
        if (newYearId && newYearId !== currentYearId) {
          setAcademicYearId(newYearId)
          yearIdRef.current = newYearId
          const label = localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''
          setAcademicYearName(label)
          changed = true
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null
        const currentSchoolId = schoolIdRef.current
        if (newSchoolId && newSchoolId !== currentSchoolId) {
          setSchoolId(newSchoolId)
          schoolIdRef.current = newSchoolId
          changed = true
        }
      }

      if (changed) {
        setSelectedStudent(null)
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadData()
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [lang, loadData])

  const filteredStudents = students.filter((s) => {
    const q = searchQuery.toLowerCase()
    return (
      s.matricule.toLowerCase().includes(q) ||
      s.first_name.toLowerCase().includes(q) ||
      s.last_name.toLowerCase().includes(q) ||
      (s.class_name || '').toLowerCase().includes(q) ||
      (s.post_bac_path || '').toLowerCase().includes(q) ||
      (s.current_city || '').toLowerCase().includes(q)
    )
  })

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-2">
            <History className="w-6 h-6 text-amber-600" />
            {$t.title}
          </h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* ═══════════════════════ HEADER ═══════════════════════ */}
      <div className="page-header">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <History className="w-6 h-6 text-amber-600" />
              {$t.title}
            </h1>
            <p className="page-subtitle">{$t.subtitle}</p>
            {/* ─── BADGE ÉTABLISSEMENT & ANNÉE ─── */}
            <div className="flex items-center gap-2 mt-1.5">
              {schoolId && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Landmark className="w-3 h-3" />
                  {$t.school} ID: {schoolId}
                </span>
              )}
              {academicYearId && academicYearName && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {academicYearName}
                </span>
              )}
              {!schoolId && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noSchool}
                </span>
              )}
              {schoolId && !academicYearId && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noYear}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/students')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            {$t.back}
          </button>
        </div>
      </div>

      {/* ═══════════════════════ MODAL DÉTAILS ═══════════════════════ */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setSelectedStudent(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {selectedStudent.first_name[0]}{selectedStudent.last_name[0]}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{selectedStudent.last_name} {selectedStudent.first_name}</h2>
                    <p className="text-amber-100 text-sm">{selectedStudent.matricule}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="p-2 hover:bg-white/20 rounded-lg text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Award className="w-3 h-3" />{$t.diploma}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedStudent.diploma_type || '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{$t.graduationYear}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedStudent.graduation_year || '—'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><BookOpen className="w-3 h-3" />{$t.postBac}</p>
                <p className="text-sm font-medium text-gray-900">{selectedStudent.post_bac_path || '—'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{$t.email}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedStudent.contact_email || '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{$t.phone}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedStudent.contact_phone || '—'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{$t.city}</p>
                <p className="text-sm font-medium text-gray-900">{selectedStudent.current_city || '—'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════ BARRE DE RECHERCHE ═══════════════════════ */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={$t.search}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ═══════════════════════ CONTENU ═══════════════════════ */}
      {students.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-16">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {$t.registry}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {$t.registryDesc}
            </p>
            <p className="text-gray-400 text-sm mt-4">
              {$t.noDataDesc}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.matricule}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.name}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.sex}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.class}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.graduationYear}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.diploma}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.postBac}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.city}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-amber-700 font-medium">{student.matricule}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 text-xs font-bold">
                            {student.first_name[0]}{student.last_name[0]}
                          </div>
                          <span className="font-medium text-gray-900">{student.last_name} {student.first_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          student.sex === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                        }`}>
                          {student.sex === 'M' ? '♂' : '♀'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{student.class_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{student.graduation_year || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          {student.diploma_type || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{student.post_bac_path || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{student.current_city || '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedStudent(student)}
                          className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-400 hover:text-amber-600 transition-colors"
                          title={$t.details}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
              <span>{filteredStudents.length} / {students.length} {lang === 'fr' ? 'ancien(s) élève(s)' : 'former student(s)'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}